/**
 * Study-level (not per-user) "open with" preference.
 *
 * Chosen once, by whichever user first clicks Open on a study card; after that
 * every user opens that study straight into the saved mode and the chooser
 * dialog is skipped for everyone — it is shared project-wide state, like
 * StudyDesignSelection, not a personal setting.
 *
 * The DB (StudyOpenPreference, one record per project — see
 * studyOpenPreference.service.js) is the source of truth. localStorage here is
 * only a fast local cache/fallback so a repeat open on the same browser doesn't
 * have to wait on a network round trip, and so the study can still open if the
 * API call fails.
 */

export const OPEN_MODE_DESIGN_STUDIO = 'DESIGN_STUDIO';
export const OPEN_MODE_DOCUMENT_AUTHORING = 'DOCUMENT_AUTHORING_TOOL';

export const OPEN_MODE_OPTIONS = [
  { value: OPEN_MODE_DESIGN_STUDIO, label: 'Open with Design Studio' },
  { value: OPEN_MODE_DOCUMENT_AUTHORING, label: 'Open with Document Authoring Tool' },
];

const VALID_MODES = OPEN_MODE_OPTIONS.map((o) => o.value);

const storageKey = (projectId) => `study_open_mode_${projectId || 'default'}`;

/** Cached mode for a study, or null when nothing is cached locally yet. */
export const getCachedStudyOpenMode = (projectId) => {
  if (!projectId) return null;
  try {
    const saved = localStorage.getItem(storageKey(projectId));
    return VALID_MODES.includes(saved) ? saved : null;
  } catch {
    return null;
  }
};

export const setCachedStudyOpenMode = (projectId, mode) => {
  if (!projectId || !VALID_MODES.includes(mode)) return;
  try {
    localStorage.setItem(storageKey(projectId), mode);
  } catch {
    /* storage unavailable (private mode / quota) — falls back to the DB next time */
  }
};

/**
 * Clears the local cache entry for a study — used after resetting the shared
 * DB preference so this browser can't keep using the old mode from a stale
 * cache while the DB is momentarily unreachable (the DB-first read always
 * wins when it succeeds, but the post-reset fallback path must not resurrect
 * the value that was just reset).
 */
export const clearCachedStudyOpenMode = (projectId) => {
  if (!projectId) return;
  try {
    localStorage.removeItem(storageKey(projectId));
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

// Back-compat aliases (pre-DB-backed naming) kept so any other call sites that
// only need the local cache keep working unchanged.
export const getStudyOpenMode = getCachedStudyOpenMode;
export const setStudyOpenMode = setCachedStudyOpenMode;
